import * as React from 'react';
import { DropdownMenuItem } from '@ooh/ui/components/dropdown-menu';
import { cn } from '@ooh/ui/lib/utils';

// ─── Props ────────────────────────────────────────────────────

export interface NotificationItemProps {
  id: number;
  title: string;
  timestamp: string;
  isRead: boolean;
  onClick: () => void;
}

// ─── Component ────────────────────────────────────────────────

/**
 * Presentational notification row inside a DropdownMenu.
 *
 * Uses DropdownMenuItem for correct role="menuitem" semantics, keyboard
 * focus management, and screen-reader accessibility.
 *
 * All strings (title, timestamp) are pre-translated by the parent.
 * Passes translated strings as props — packages/ui stays translation-free.
 *
 * data-testid="notification-item-{id}" is required for E2E tests (task 5.1).
 * data-read attribute supports both visual styling and test assertions.
 */
export function NotificationItem({ id, title, timestamp, isRead, onClick }: NotificationItemProps) {
  return (
    <DropdownMenuItem
      data-testid={`notification-item-${id}`}
      data-read={String(isRead)}
      onClick={onClick}
      className={cn(
        'flex h-auto w-full flex-col items-start gap-0.5 rounded-none px-3 py-2.5 text-left cursor-pointer',
        !isRead && 'bg-accent/30',
      )}
    >
      <span
        className={cn(
          'w-full text-sm leading-snug',
          isRead ? 'text-muted-foreground' : 'text-foreground font-medium',
        )}
      >
        {title}
      </span>
      <span className="w-full text-xs text-muted-foreground">{timestamp}</span>
    </DropdownMenuItem>
  );
}
